/* ============================================================
   ZonaPhone PDP — main script
   Spec: docs/superpowers/specs/2026-04-29-pdp-rediseno-design.md §3.3
   ============================================================ */
(function () {
  'use strict';

  // Textos traducibles: window.zpLang lo emite Liquid (theme.liquid) con las
  // claves zp_pdp.* del locale activo. Fallback = literal español original,
  // así el script funciona igual aunque el puente no exista.
  function zpT(key, fallback) {
    return (window.zpLang && window.zpLang[key]) || fallback;
  }

  // productData vive en el scope del IIFE para que sea accesible desde
  // helpers como renderBuyBox/isPreorder, no solo desde DOMContentLoaded.
  var productData = null;

  // Listeners a nivel document registrados por el boot. El swap SIM/eSIM
  // vuelve a arrancar el script sobre un DOM nuevo: los listeners atados a
  // nodos mueren con ellos, pero los de document se acumularían. Se
  // registran aquí y bootPdp los retira antes de volver a añadirlos.
  var docListeners = [];
  function onDocument(type, fn) {
    document.addEventListener(type, fn);
    docListeners.push({ type: type, fn: fn });
  }
  function offDocumentAll() {
    docListeners.forEach(function (l) { document.removeEventListener(l.type, l.fn); });
    docListeners = [];
  }

  // =====================================================
  // 1. Boot: parse product data inyectado por Liquid
  // =====================================================
  function readProductData() {
    var el = document.getElementById('zp-product-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[zp-pdp] failed to parse product data', e);
      return null;
    }
  }

  // =====================================================
  // 2. Mapeo nombre opción → option1/option2/option3
  //    (para que reordenar opciones en admin no rompa el JS)
  // =====================================================
  // Identifica el eje canónico de una opción por sus VALORES, no por su nombre.
  // Shopify traduce el nombre de la opción por mercado (Color->Cor,
  // Almacenamiento->Armazenamento en pt-PT). Matchear por nombre rompía color y
  // capacidad en /pt (los selectores desaparecían).
  //
  // OJO: los VALORES tampoco son intocables. La traducción automática de
  // Translate & Adapt escribe sobre ellos — ya convirtió '512GB' en '512 GB'.
  // Por eso el matching de abajo lleva alias por idioma y normaliza acentos,
  // en vez de dar por hecho que el valor llega en español.
  // Normaliza a MAYUSCULAS sin acentos: 'Satisfatório' -> 'SATISFATORIO'.
  function normalizarValor(v) {
    var s = String(v).toUpperCase();
    return s.normalize ? s.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : s;
  }

  function classifyOptionAxis(opt) {
    var vals = (opt && opt.values ? opt.values : []).map(normalizarValor);
    var isStorage = vals.some(function (v) {
      return v.indexOf('GB') !== -1 || v.indexOf('TB') !== -1;
    });
    if (isStorage) return 'Almacenamiento';
    // Alias por idioma (es, pt-PT, en). 'PREMIUM' y 'EXCELENTE' se escriben igual en pt-PT y
    // los 37 productos con grado los llevan, asi que el eje se reconoce aunque
    // el traductor toque los otros dos. El resto va como red de seguridad.
    var GRADES = ['PREMIUM', 'EXCELENTE', 'MUY BUENO', 'SATISFACTORIO',
                  'MUITO BOM', 'SATISFATORIO',
                  'EXCELLENT', 'VERY GOOD', 'SATISFACTORY', 'FAIR'];
    var isGrade = vals.some(function (v) { return GRADES.indexOf(v) !== -1; });
    if (isGrade) return 'Estado estético';
    return 'Color';
  }

  function buildOptionIndex(productData) {
    var idx = {};
    if (!productData || !productData.options_with_values) return idx;
    productData.options_with_values.forEach(function (opt, i) {
      var key = 'option' + (i + 1);
      // Nombre tal cual (posiblemente traducido) por compatibilidad...
      idx[opt.name] = key;
      // ...y el eje canónico detectado por valores, que es lo que usa el resto
      // del JS (optionIndex['Color'], ['Almacenamiento'], ['Estado estético']).
      var axis = classifyOptionAxis(opt);
      if (!idx[axis]) idx[axis] = key;
    });
    return idx;
  }

  // =====================================================
  // 3. State + initial selection
  // =====================================================
  function initialStateFromVariant(variant, optionIndex) {
    return {
      selectedAlmacenamiento: variant ? variant[optionIndex['Almacenamiento']] : null,
      selectedEstadoEstetico: variant ? variant[optionIndex['Estado estético']] : null,
      selectedColor:          variant ? variant[optionIndex['Color']] : null,
      addBattery: false,
      batteryPrice: 0,  // precio del addon de batería en cents (lo llena bindBatteryAddon)
      qty: 1
    };
  }

  // =====================================================
  // 4. Find variant matching state
  // =====================================================
  function findVariant(state, productData, optionIndex) {
    if (!productData) return null;
    return productData.variants.find(function (v) {
      return v[optionIndex['Almacenamiento']] === state.selectedAlmacenamiento
          && v[optionIndex['Estado estético']] === state.selectedEstadoEstetico
          && v[optionIndex['Color']]           === state.selectedColor;
    }) || null;
  }

  // =====================================================
  // 4b. Detección de preventa
  // Una variante está en preventa si:
  //   - inventory_policy === 'continue' (seguir vendiendo sin stock)
  //   - inventory_quantity <= 0 (no hay stock real)
  // Cuando la variante recibe stock real, vuelve a venta normal sola.
  //
  // OJO: `product.variants | json` de Shopify NO incluye inventory_policy
  // ni inventory_quantity. Por eso leemos del mapa `variantInventory` que
  // genera la sección PDP en sections/main-product-zonaphone.liquid.
  // =====================================================
  function isPreorder(variant, productData) {
    if (!variant) return false;
    var inv = productData && productData.variantInventory && productData.variantInventory[variant.id];
    if (!inv) return false;
    return inv.policy === 'continue' && (inv.quantity == null || inv.quantity <= 0);
  }

  // =====================================================
  // 5. Initial variant from URL ?variant= or first available
  // =====================================================
  function pickInitialVariant(productData) {
    if (!productData || !productData.variants.length) return null;
    var params = new URLSearchParams(window.location.search);
    var requestedId = parseInt(params.get('variant'), 10);
    if (requestedId) {
      var found = productData.variants.find(function (v) { return v.id === requestedId; });
      if (found) return found;
    }
    // Default: first available, fallback to first
    return productData.variants.find(function (v) { return v.available; }) || productData.variants[0];
  }

  // =====================================================
  // BOOT
  // =====================================================
  function bootPdp() {
    // Leer ANTES de retirar nada: si el JSON viene roto, la página se queda
    // con los listeners que ya tenía en vez de quedarse muda.
    var data = readProductData();
    if (!data) {
      console.warn('[zp-pdp] no product data found, JS disabled');
      return;
    }
    offDocumentAll();
    productData = data;
    var optionIndex = buildOptionIndex(productData);
    var initialVariant = pickInitialVariant(productData);
    var state = initialStateFromVariant(initialVariant, optionIndex);

    // Expose for console debugging in dev (only if ?zpdebug=1)
    if (window.location.search.indexOf('zpdebug=1') !== -1) {
      window.__zpPdp = { state: state, productData: productData, optionIndex: optionIndex, findVariant: findVariant };
      console.log('[zp-pdp] debug mode on. Inspect window.__zpPdp');
    }

    // =================================================
    // Wire del buy box (Phase 2)
    // =================================================
    var currentVariant = initialVariant;
    function getCurrentVariant() { return currentVariant; }

    renderBuyBox(state, currentVariant);
    bindQtyStepper(state, getCurrentVariant);
    bindCtas(state, getCurrentVariant, productData);
    renderSimSelector(state, currentVariant);
    bindSimSelector();

    // =================================================
    // Wire del Grade Picker (Phase 3)
    // =================================================
    function onSelectionChange() {
      // Si había un swap SIM/eSIM en vuelo, el usuario ya cambió de idea:
      // abortarlo antes de repintar (renderBuyBox devuelve las CTAs a su
      // estado real según la nueva variante).
      cancelSwap();
      currentVariant = findVariant(state, productData, optionIndex);
      renderBuyBox(state, currentVariant);
      renderGradePicker(state, productData, optionIndex);
      renderQuickInfo(state, productData, optionIndex);
      renderGradePill(state);
      renderSimSelector(state, currentVariant);
      syncGalleryToVariant(currentVariant);
      updateUrl(currentVariant);
      document.dispatchEvent(new CustomEvent('zp:variant-change', { detail: { state: state, variant: currentVariant } }));
    }

    renderGradePicker(state, productData, optionIndex);
    bindGradePicker(state, productData, optionIndex, onSelectionChange);

    // =================================================
    // Wire del Quick Info (Phase 4)
    // =================================================
    renderQuickInfo(state, productData, optionIndex);
    bindQuickInfo(state, productData, optionIndex, onSelectionChange);

    // =================================================
    // Wire de la Galería (Phase 4)
    // =================================================
    bindGalleryThumbs(state, productData, optionIndex, onSelectionChange);
    // Sincronizar la foto inicial con la variante elegida (primera DISPONIBLE).
    // El Liquid ya marca la slide correcta como activa, pero esto cubre el caso
    // de HTML cacheado o de un ?variant= que difiera del render del server.
    syncGalleryToVariant(currentVariant);
    renderGradePill(state);
    onDocument('click', function (e) {
      if (e.target.closest && e.target.closest('[data-zp-grade-jump]')) jumpToGradePicker();
    });

    // =================================================
    // Wire Battery Addon (Phase 5)
    // =================================================
    bindBatteryAddon(state);
    // Cuando el usuario marca/desmarca la batería, re-renderear precio + savings + financiación
    onDocument('zp:battery-toggle', function () {
      renderBuyBox(state, currentVariant);
    });

    if (window.__zpPdp) {
      window.__zpPdp.renderBuyBox = renderBuyBox;
      window.__zpPdp.formatMoney = formatMoney;
      window.__zpPdp.getCurrentVariant = getCurrentVariant;
      window.__zpPdp.renderGradePicker = renderGradePicker;
      window.__zpPdp.recomputeAvailability = recomputeAvailability;
      window.__zpPdp.onSelectionChange = onSelectionChange;
      window.__zpPdp.snapToAvailable = snapToAvailable;
      window.__zpPdp.altColorVariantForGrade = altColorVariantForGrade;
      window.__zpPdp.altVariantForStorage = altVariantForStorage;
      window.__zpPdp.comboAvailable = comboAvailable;
      window.__zpPdp.twinVariantFor = twinVariantFor;
      window.__zpPdp.canSwapInPlace = canSwapInPlace;
      window.__zpPdp.bootPdp = bootPdp;
      window.__zpPdp.renderSimSelector = renderSimSelector;
      window.__zpPdp.swapToTwin = swapToTwin;
      window.__zpPdp.shouldAutoSwap = shouldAutoSwap;
    }

    // Lo último del boot: si esta ficha no tiene nada que vender y el gemelo sí,
    // cambiamos solos. Va al final para que el swap salga con todo ya pintado.
    maybeAutoSwapToTwin();
  }
  document.addEventListener('DOMContentLoaded', bootPdp);

  // ===========================================================
  // 6. Helpers de render del buy box (definidos dentro del IIFE,
  //    fuera del DOMContentLoaded para que sean callables desde
  //    onSelectionChange en futuras phases)
  // ===========================================================
  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '—';
    var euros = (cents / 100).toFixed(2);
    var parts = euros.split('.');
    var intWithSep = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return intWithSep + ',' + parts[1] + ' €';
  }

  // Igual que formatMoney pero sin ",00" cuando el importe es redondo
  // (1.209 € en vez de 1.209,00 €). Para el botón de compra, donde los
  // decimales hacían el precio más largo que el propio texto.
  function formatMoneyShort(cents) {
    var full = formatMoney(cents);
    return full.replace(/,00 €$/, ' €');
  }

  // Precio nuevo (lanzamiento de Apple en España) para la capacidad elegida,
  // en céntimos; 0 si el producto no lo tiene. El metafield guarda euros por
  // capacidad: {"128GB": 1219, "256GB": 1349}. La clave se normaliza porque la
  // traducción automática escribe "512 GB" en otros idiomas.
  function newPriceFor(state) {
    var map = productData && productData.product && productData.product.newPrices;
    if (!map || !state || !state.selectedAlmacenamiento) return 0;
    var key = String(state.selectedAlmacenamiento).replace(/\s+/g, '').toUpperCase();
    var euros = parseFloat(map[key]);
    return euros > 0 ? Math.round(euros * 100) : 0;
  }

  function renderBuyBox(state, variant) {
    // Precio block — vive duplicado en 2 lugares según viewport:
    //   Mobile: Quick Info (bajo el título) → data-zp-qi-*
    //   Desktop: Buy Box (columna derecha sticky) → data-zp-bb-*
    // CSS controla visibilidad, JS actualiza ambos en sync.
    var qiPrice         = document.querySelector('[data-zp-qi-price]');
    var qiCompare       = document.querySelector('[data-zp-qi-compare]');
    var qiSavingsBadge  = document.querySelector('[data-zp-qi-savings]');
    var qiSavingsText   = document.querySelector('[data-zp-qi-savings-text]');
    var qiFinance       = document.querySelector('[data-zp-qi-finance]');
    var qiStockText     = document.querySelector('[data-zp-qi-stock-text]');
    var qiStock         = qiStockText ? qiStockText.closest('.zp-pdp__qi-stock') : null;
    var bbPrice         = document.querySelector('[data-zp-bb-price]');
    var bbCompare       = document.querySelector('[data-zp-bb-compare]');
    var bbSavingsBadge  = document.querySelector('[data-zp-bb-savings]');
    var bbSavingsText   = document.querySelector('[data-zp-bb-savings-text]');
    var bbFinance       = document.querySelector('[data-zp-bb-finance]');
    var bbStockText     = document.querySelector('[data-zp-bb-stock-text]');
    var bbStock         = bbStockText ? bbStockText.closest('.zp-pdp__bb-stock') : null;

    // Buy box: solo CTAs, qty, sticky footer
    var ctaPrimaryEl = document.querySelector('[data-zp-add-to-cart]');
    var ctaSecondaryEl = document.querySelector('[data-zp-buy-now]');
    var qtyInput = document.querySelector('[data-zp-qty-input]');
    var qtyMinus = document.querySelector('[data-zp-qty-minus]');
    var qtyPlus  = document.querySelector('[data-zp-qty-plus]');
    var stickyPrice = document.querySelector('[data-zp-sticky-price]');
    var stickyCta = document.querySelector('[data-zp-sticky-cta]');

    if (!variant) {
      if (ctaPrimaryEl) { ctaPrimaryEl.disabled = true; ctaPrimaryEl.textContent = zpT('stockOut', 'Sin stock'); }
      if (ctaSecondaryEl) ctaSecondaryEl.disabled = true;
      if (stickyCta) { stickyCta.disabled = true; stickyCta.textContent = zpT('stockOut', 'Sin stock'); }
      return;
    }

    // ── Precio total (con o sin batería opcional) ──
    var batteryExtra = (state.addBattery && state.batteryPrice) ? state.batteryPrice : 0;
    var totalPrice = variant.price + batteryExtra;
    var formattedPrice = formatMoney(totalPrice);

    if (qiPrice) qiPrice.textContent = formattedPrice;
    if (bbPrice) bbPrice.textContent = formattedPrice;
    if (stickyPrice) stickyPrice.textContent = formattedPrice;

    // Sticky footer: variant text "128GB · Premium · Celeste"
    var stickyVariant = document.querySelector('[data-zp-sticky-variant]');
    if (stickyVariant && variant.title) {
      stickyVariant.textContent = variant.title.replace(/ \/ /g, ' · ');
    }
    // Resumen de la variante bajo el título (ficha A)
    var qiVariant = document.querySelector('[data-zp-qi-variant]');
    if (qiVariant && variant.title) {
      qiVariant.textContent = variant.title.replace(/ \/ /g, ' · ');
    }

    // ── Compare price + savings badge ──
    // Solo precios de referencia REALES (oct-2026). Antes, a falta de un
    // compare_at_price ≥ 1,4×, se inventaba un "precio nuevo" de precio × 1,6.
    //  1. Precio nuevo: el de lanzamiento de Apple en España para la capacidad
    //     elegida (metafield custom.precio_nuevo del producto, como hace Back
    //     Market). Texto "Ahorras X vs nuevo".
    //  2. Si no lo hay, la rebaja: compare_at_price del admin > precio. Es el
    //     precio anterior, no el de Apple, así que el texto no dice "vs nuevo".
    //  3. Sin ninguno: ni tachado ni ahorro.
    // Pre-Black Friday (nov-2026): en los equipos de la campaña la rebaja va
    // PRIMERO (lo que se anuncia es la bajada de precio, no el «vs nuevo») y
    // debajo sale «precio más bajo de los últimos 30 días» = el tachado
    // (directiva Omnibus). productData.product.preBlackFriday lo pone el Liquid
    // con la campaña activa y el tag; llega también al cambiar a SIM/eSIM.
    // Con la batería marcada el tachado incluye la batería, para comparar lo
    // mismo con lo mismo; el ahorro es solo el del teléfono.
    var newPriceCents = newPriceFor(state);
    var compareAt = variant.compare_at_price;
    var refPrice = 0;
    var vsNew = false;
    var pbfSale = !!(productData && productData.product && productData.product.preBlackFriday) &&
      !!compareAt && compareAt > variant.price;
    if (pbfSale) {
      refPrice = compareAt;
    } else if (newPriceCents > variant.price) {
      refPrice = newPriceCents;
      vsNew = true;
    } else if (compareAt && compareAt > variant.price) {
      refPrice = compareAt;
    }
    var hasCompare = refPrice > 0;
    var savings = hasCompare ? refPrice - variant.price : 0;
    var pct = hasCompare ? Math.round((savings / refPrice) * 100) : 0;
    var formattedCompare = hasCompare ? formatMoney(refPrice + batteryExtra) : '';
    var savingsText = (vsNew
      ? zpT('savingsVsNew', 'Ahorras [[amount]] vs nuevo (-[[percent]]%)')
      : zpT('savings', 'Ahorras [[amount]] (-[[percent]]%)'))
      .replace('[[amount]]', formatMoney(savings))
      .replace('[[percent]]', pct);

    if (qiCompare) qiCompare.textContent = formattedCompare;
    if (bbCompare) bbCompare.textContent = formattedCompare;

    // La caja «Este precio termina en…» solo con la variante elegida rebajada.
    var pbfBox = document.querySelector('[data-zp-pbf-box]');
    if (pbfBox) pbfBox.hidden = !pbfSale;

    var qiLowest = document.querySelector('[data-zp-qi-lowest]');
    if (qiLowest) {
      if (pbfSale) {
        qiLowest.textContent = zpT('lowest30', 'Precio más bajo de los últimos 30 días: [[amount]]')
          .replace('[[amount]]', formatMoney(compareAt + batteryExtra));
        qiLowest.hidden = false;
      } else {
        qiLowest.textContent = '';
        qiLowest.hidden = true;
      }
    }

    if (qiSavingsBadge && qiSavingsText) {
      if (savings > 0) { qiSavingsText.textContent = savingsText; qiSavingsBadge.hidden = false; }
      else { qiSavingsBadge.hidden = true; }
    }
    if (bbSavingsBadge && bbSavingsText) {
      if (savings > 0) { bbSavingsText.textContent = savingsText; bbSavingsBadge.hidden = false; }
      else { bbSavingsBadge.hidden = true; }
    }

    // ── Financiación (sobre el total) ──
    var financeHtml = '';
    if (totalPrice >= 10000) {
      var monthly = Math.round(totalPrice / 24);
      financeHtml = zpT('financeMonthlyHtml', 'Desde <strong>[[amount]]/mes</strong> en 24 cuotas')
        .replace('[[amount]]', formatMoney(monthly));
    }
    if (qiFinance) qiFinance.innerHTML = financeHtml;
    if (bbFinance) bbFinance.innerHTML = financeHtml;

    // ── Stock dot + texto ──
    var preorder = isPreorder(variant, productData);
    var stockHtml, stockOut;
    if (preorder) {
      stockHtml = zpT('stockPreorderHtml', '<strong>Disponible en preventa</strong>');
      stockOut = false;
    } else if (variant.available) {
      stockHtml = zpT('stockInHtml', '<strong>En stock</strong> · listo para enviar');
      stockOut = false;
    } else {
      stockHtml = zpT('stockOut', 'Sin stock');
      stockOut = true;
    }
    if (qiStockText && qiStock) {
      qiStockText.innerHTML = stockHtml;
      qiStock.classList.toggle('zp-pdp__qi-stock--out', stockOut);
      qiStock.classList.toggle('zp-pdp__qi-stock--preorder', preorder);
    }
    if (bbStockText && bbStock) {
      bbStockText.innerHTML = stockHtml;
      bbStock.classList.toggle('zp-pdp__bb-stock--out', stockOut);
      bbStock.classList.toggle('zp-pdp__bb-stock--preorder', preorder);
    }

    // ── Aviso de preventa (toggle según variante seleccionada) ──
    var preorderNotice = document.querySelector('[data-zp-preorder-notice]');
    if (preorderNotice) preorderNotice.hidden = !preorder;

    var maxQty = Math.min(10, variant.inventory_quantity > 0 ? variant.inventory_quantity : 10);
    if (qtyInput) {
      qtyInput.max = maxQty;
      if (parseInt(qtyInput.value, 10) > maxQty) qtyInput.value = maxQty;
      state.qty = parseInt(qtyInput.value, 10);
    }
    if (qtyMinus) qtyMinus.disabled = state.qty <= 1;
    if (qtyPlus)  qtyPlus.disabled  = state.qty >= maxQty;

    var preorderCtaText = (ctaPrimaryEl && ctaPrimaryEl.dataset.zpPreorderText) || zpT('reserve', 'Reservar ahora');
    var preorderStickyText = (stickyCta && stickyCta.dataset.zpPreorderStickyText) || zpT('reserveShort', 'Reservar');

    if (ctaPrimaryEl) {
      ctaPrimaryEl.disabled = !variant.available;
      if (preorder) {
        ctaPrimaryEl.textContent = preorderCtaText;
      } else if (variant.available) {
        // Ficha A: el botón lleva el total (con batería si está marcada), así
        // el precio no tiene que repetirse en otro bloque junto al botón.
        // Texto y precio separados por una raya fina (antes " · "), y el
        // precio sin ",00": Santiago lo veía raro y demasiado largo.
        ctaPrimaryEl.textContent = '';
        ctaPrimaryEl.appendChild(document.createTextNode(zpT('addToCart', 'Añadir al carrito') + ' '));
        var ctaSep = document.createElement('span');
        ctaSep.className = 'zp-pdp__cta-sep';
        ctaSep.setAttribute('aria-hidden', 'true');
        ctaPrimaryEl.appendChild(ctaSep);
        ctaPrimaryEl.appendChild(document.createTextNode(' ' + formatMoneyShort(totalPrice)));
      } else {
        ctaPrimaryEl.textContent = zpT('stockOut', 'Sin stock');
      }
      ctaPrimaryEl.classList.toggle('zp-pdp__cta--preorder', preorder);
    }
    if (ctaSecondaryEl) {
      ctaSecondaryEl.disabled = !variant.available;
      if (preorder) {
        ctaSecondaryEl.textContent = zpT('reserve', 'Reservar ahora');
      } else {
        ctaSecondaryEl.textContent = zpT('buyNow', 'Comprar ahora');
      }
    }
    if (stickyCta) {
      stickyCta.disabled = !variant.available;
      var ctaLabel;
      if (preorder) ctaLabel = preorderStickyText;
      else ctaLabel = variant.available ? zpT('addToCart', 'Añadir al carrito') : zpT('soldOut', 'Agotado');
      var stickyText = stickyCta.querySelector('.zp-pdp__sticky-btn-text');
      if (stickyText) {
        stickyText.textContent = ctaLabel;
      } else {
        stickyCta.textContent = ctaLabel;
      }
      stickyCta.classList.toggle('zp-pdp__sticky-btn--preorder', preorder);
    }
  }

  function updateUrl(variant) {
    if (!variant) return;
    var params = new URLSearchParams(window.location.search);
    params.set('variant', variant.id);
    var newUrl = window.location.pathname + '?' + params.toString();
    history.replaceState(null, '', newUrl);
  }

  function bindQtyStepper(state, getCurrentVariant) {
    var input = document.querySelector('[data-zp-qty-input]');
    var minus = document.querySelector('[data-zp-qty-minus]');
    var plus  = document.querySelector('[data-zp-qty-plus]');
    if (!input) return;

    function setQty(n) {
      var max = parseInt(input.max, 10) || 10;
      n = Math.max(1, Math.min(max, n));
      input.value = n;
      state.qty = n;
      renderBuyBox(state, getCurrentVariant());
    }
    if (minus) minus.addEventListener('click', function () { setQty(state.qty - 1); });
    if (plus)  plus.addEventListener('click',  function () { setQty(state.qty + 1); });
    input.addEventListener('change', function () { setQty(parseInt(input.value, 10) || 1); });
  }

  // ===========================================================
  // 7. AJAX add to cart
  // ===========================================================
  function addToCart(payload) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (err) { throw err; });
      }
      return r.json();
    });
  }

  function buildCartPayload(state, variant, batteryVariantId, productData) {
    var items = [{ id: variant.id, quantity: state.qty }];
    if (state.addBattery && batteryVariantId) {
      var forText = (productData && productData.product && productData.product.title)
        ? productData.product.title + ' (' + variant.title + ')'
        : (variant.title || 'Producto principal');
      items.push({
        id: batteryVariantId,
        quantity: state.qty,
        properties: { 'Para': forText }
      });
    }
    return { items: items };
  }

  function showCartError(msg) {
    var existing = document.querySelector('.zp-pdp__toast');
    if (existing) existing.remove();
    var toast = document.createElement('div');
    toast.className = 'zp-pdp__toast';
    toast.setAttribute('role', 'alert');
    toast.textContent = msg;
    document.body.appendChild(toast);
    setTimeout(function () { toast.remove(); }, 4000);
  }

  function bindCtas(state, getCurrentVariant, productData) {
    var primary = document.querySelector('[data-zp-add-to-cart]');
    var secondary = document.querySelector('[data-zp-buy-now]');
    var stickyCta = document.querySelector('[data-zp-sticky-cta]');

    function handleAdd(redirectAfter, qtyOverride) {
      var variant = getCurrentVariant();
      if (!variant || !variant.available) return;
      var prevQty = state.qty;
      if (typeof qtyOverride === 'number') state.qty = qtyOverride;

      var batteryVariantId = window.__zpBatteryVariantId || null;
      var payload = buildCartPayload(state, variant, batteryVariantId, productData);

      // Loading state: primary (desktop) + sticky (mobile)
      if (primary) { primary.disabled = true; primary.textContent = 'Añadiendo...'; }
      if (stickyCta) { stickyCta.disabled = true; stickyCta.classList.add('is-loading'); }

      addToCart(payload).then(function (data) {
        // El theme escucha eventos vía ft-labs/delegate en document.documentElement.
        // Si dispatchamos en `document`, el evento NO bubbleae hasta documentElement
        // (que es child de document), así que el drawer no se abre.
        // Solución: dispatch directo en documentElement con bubbles:true.
        document.documentElement.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: { button: primary, variant: null, quantity: state.qty, productAdded: true }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { quantity: state.qty }
        }));
        state.qty = prevQty;
        if (redirectAfter) {
          window.location.href = '/checkout';
        } else {
          if (stickyCta) stickyCta.classList.remove('is-loading');
          // Devuelve los botones a su estado real (texto con el precio incluido).
          renderBuyBox(state, getCurrentVariant());
        }
      }).catch(function (err) {
        var msg = (err && err.description) || (err && err.message);
        if (!msg || msg === 'Failed to fetch') {
          msg = zpT('cartError', 'No se pudo añadir al carrito. Comprueba tu conexión.');
        }
        showCartError(msg);
        if (stickyCta) stickyCta.classList.remove('is-loading');
        state.qty = prevQty;
        renderBuyBox(state, getCurrentVariant());
      });
    }

    if (primary)   primary.addEventListener('click', function () { handleAdd(false); });
    if (secondary) secondary.addEventListener('click', function () { handleAdd(true); });
    if (stickyCta) stickyCta.addEventListener('click', function () {
      // "Añadir al carrito" mobile: añade qty=1, abre el drawer (NO redirect a checkout).
      // Permite ver el addon de batería + sumar accesorios + Abandoned Cart Recovery.
      handleAdd(false, 1);
    });
  }

  // ===========================================================
  // 12. Grade Picker — repricing por variante (Phase 3)
  // ===========================================================
  function priceForGrade(state, gradeValue, productData, optionIndex) {
    if (!productData || !optionIndex['Estado estético']) return null;
    return productData.variants.find(function (v) {
      return v[optionIndex['Estado estético']] === gradeValue
          && v[optionIndex['Almacenamiento']]   === state.selectedAlmacenamiento
          && v[optionIndex['Color']]            === state.selectedColor;
    }) || null;
  }

  function gradeStockBadge(variant) {
    if (!variant || !variant.available) return { text: zpT('stockOut', 'Sin stock'), cls: 'zp-pdp__grade-stock--out' };
    var qty = variant.inventory_quantity;
    // Si inventory_management es null/false, Shopify no rastrea stock — asumimos disponible sin número
    if (typeof qty !== 'number' || qty <= 0) return { text: zpT('stockAvailable', 'Disponible'), cls: '' };
    if (qty >= 7) return { text: zpT('stockNAvailable', '[[qty]] disponibles').replace('[[qty]]', qty), cls: '' };
    return { text: zpT('onlyLeft', '¡Solo [[qty]]!').replace('[[qty]]', qty), cls: 'zp-pdp__grade-stock--low' };
  }

  // ===========================================================
  // 13. Disponibilidad inteligente (greying out)
  //
  // JERÁRQUICA, siguiendo el orden visual de la página:
  //   Color > Almacenamiento > Estado estético.
  // Cada eje se evalúa ignorando los ejes INFERIORES:
  //   - Color: ¿algún stock de ese color en CUALQUIER capacidad/grado?
  //   - Almacenamiento: ¿algún stock de (color elegido + esa capacidad)
  //     en cualquier grado?
  //   - Estado estético: exacto (color + capacidad + grado) — último nivel,
  //     es el que muestra precio y AGOTADO.
  //
  // Antes cada eje se cruzaba con TODO lo seleccionado y escondía stock:
  // parado en Negro-Premium, "Dorado" se tachaba aunque tuviera Excelente
  // disponible → el cliente creía que no quedaba nada (bug reportado en el
  // iPhone 14 Pro Max eSIM: solo 1 de 5 variantes con stock se veía viva).
  // ===========================================================
  var ZP_AXIS_ABOVE = {
    'Color': [],
    'Almacenamiento': ['Color'],
    'Estado estético': ['Color', 'Almacenamiento']
  };
  var ZP_STATE_KEY = {
    'Almacenamiento': 'selectedAlmacenamiento',
    'Estado estético': 'selectedEstadoEstetico',
    'Color': 'selectedColor'
  };

  function recomputeAvailability(state, productData, optionIndex) {
    var axes = ['Almacenamiento', 'Estado estético', 'Color'];
    var result = {};
    if (!productData || !productData.options_with_values) return result;

    axes.forEach(function (axis) {
      result[axis] = {};
      var optKey = optionIndex[axis];
      if (!optKey) return;
      // optKey es 'optionN' → la opción vive en la posición N-1 del array.
      // No buscar por o.name === axis: el nombre viene traducido en /pt.
      var optPos = parseInt(optKey.replace('option', ''), 10);
      var optInfo = productData.options_with_values[optPos - 1];
      if (!optInfo) return;

      // Solo restringen los ejes SUPERIORES en la jerarquía (y que existan
      // en este producto): los inferiores se ignoran para no esconder stock.
      var constraints = ZP_AXIS_ABOVE[axis].filter(function (a) {
        return !!optionIndex[a];
      });

      optInfo.values.forEach(function (val) {
        result[axis][val] = productData.variants.some(function (v) {
          if (v[optKey] !== val) return false;
          var ok = constraints.every(function (a) {
            return v[optionIndex[a]] === state[ZP_STATE_KEY[a]];
          });
          return ok && v.available;
        });
      });
    });
    return result;
  }

  // ===========================================================
  // 13b. Auto-salto a una combinación comprable
  //
  // Tras un clic en color o capacidad, si la combinación resultante no
  // tiene stock, ajusta SOLO los ejes inferiores (capacidad y/o grado)
  // hasta aterrizar en una variante disponible. Preferencia: mantener lo
  // ya elegido; si no, orden de display de la opción. Así, tocar "Deep
  // Purple" desde Negro-Premium te deja en Deep Purple-Excelente listo
  // para comprar, nunca en "Premium AGOTADO · Sin stock".
  //
  // Si el valor clicado no tiene stock en NINGUNA combinación (venía
  // tachado), no toca nada: el buy box muestra "Sin stock" honesto.
  // ===========================================================
  function optionValuesForAxis(axis, productData, optionIndex) {
    var optKey = optionIndex[axis];
    if (!optKey || !productData || !productData.options_with_values) return [];
    var optPos = parseInt(optKey.replace('option', ''), 10);
    var optInfo = productData.options_with_values[optPos - 1];
    return (optInfo && optInfo.values) ? optInfo.values : [];
  }

  function comboAvailable(productData, optionIndex, color, storage, grade) {
    if (!productData) return false;
    return productData.variants.some(function (v) {
      if (optionIndex['Color'] && v[optionIndex['Color']] !== color) return false;
      if (optionIndex['Almacenamiento'] && v[optionIndex['Almacenamiento']] !== storage) return false;
      if (optionIndex['Estado estético'] && v[optionIndex['Estado estético']] !== grade) return false;
      return v.available;
    });
  }

  function preferCurrentFirst(current, values) {
    // [valor actual, ...resto en orden de display]
    var rest = values.filter(function (v) { return v !== current; });
    return current != null ? [current].concat(rest) : rest.length ? rest : values;
  }

  function snapToAvailable(state, changedAxis, productData, optionIndex) {
    if (comboAvailable(productData, optionIndex, state.selectedColor, state.selectedAlmacenamiento, state.selectedEstadoEstetico)) {
      return; // la combinación ya es comprable, no tocar nada
    }
    // Un clic en Color puede reajustar capacidad y grado; un clic en
    // capacidad solo el grado. (El grado es el último eje: no ajusta nada.)
    var storages = changedAxis === 'Color'
      ? preferCurrentFirst(state.selectedAlmacenamiento, optionValuesForAxis('Almacenamiento', productData, optionIndex))
      : [state.selectedAlmacenamiento];
    var grades = preferCurrentFirst(state.selectedEstadoEstetico, optionValuesForAxis('Estado estético', productData, optionIndex));
    if (!storages.length) storages = [state.selectedAlmacenamiento];
    if (!grades.length) grades = [state.selectedEstadoEstetico];

    for (var s = 0; s < storages.length; s++) {
      for (var g = 0; g < grades.length; g++) {
        if (comboAvailable(productData, optionIndex, state.selectedColor, storages[s], grades[g])) {
          state.selectedAlmacenamiento = storages[s];
          state.selectedEstadoEstetico = grades[g];
          return;
        }
      }
    }
    // Sin stock en todo el eje clicado (venía tachado): estado queda como está.
  }

  // ===========================================================
  // 14b. Grado agotado en el color actual pero disponible en OTRO color
  //      (con la capacidad actual). Devuelve la primera variante disponible
  //      siguiendo el orden de display de los colores, o null.
  //      Alimenta el "aviso + salto" del grade picker: la fila muestra
  //      "Disponible en [color]" y al tocarla cambia a ese color.
  // ===========================================================
  function altColorVariantForGrade(state, gradeValue, productData, optionIndex) {
    if (!productData || !optionIndex['Color'] || !optionIndex['Estado estético']) return null;
    var colors = optionValuesForAxis('Color', productData, optionIndex);
    for (var i = 0; i < colors.length; i++) {
      var c = colors[i];
      if (c === state.selectedColor) continue;
      var match = productData.variants.find(function (v) {
        if (v[optionIndex['Color']] !== c) return false;
        if (v[optionIndex['Estado estético']] !== gradeValue) return false;
        if (optionIndex['Almacenamiento'] && v[optionIndex['Almacenamiento']] !== state.selectedAlmacenamiento) return false;
        return v.available;
      });
      if (match) return match;
    }
    return null;
  }

  // ===========================================================
  // 14c. Capacidad agotada en el color actual pero disponible en OTRO color.
  //      Mismo patrón "aviso + salto" que el grade picker, aplicado al chip
  //      de capacidad: el chip anuncia "en otro color" y el clic salta a esa
  //      combinación comprable. Preferencia: mantener el GRADO actual (el
  //      cliente lo eligió a propósito; el color va a cambiar igual), sino
  //      orden de display de grados; dentro de cada grado, colores en orden
  //      de display. Devuelve la variante disponible o null.
  // ===========================================================
  function altVariantForStorage(state, storageValue, productData, optionIndex) {
    if (!productData || !optionIndex['Color'] || !optionIndex['Almacenamiento']) return null;
    var colors = optionValuesForAxis('Color', productData, optionIndex);
    var grades = optionIndex['Estado estético']
      ? preferCurrentFirst(state.selectedEstadoEstetico, optionValuesForAxis('Estado estético', productData, optionIndex))
      : [null];
    if (!grades.length) grades = [null];
    for (var g = 0; g < grades.length; g++) {
      for (var i = 0; i < colors.length; i++) {
        var c = colors[i];
        if (c === state.selectedColor) continue;
        var grade = grades[g];
        var match = productData.variants.find(function (v) {
          if (v[optionIndex['Color']] !== c) return false;
          if (v[optionIndex['Almacenamiento']] !== storageValue) return false;
          if (grade != null && v[optionIndex['Estado estético']] !== grade) return false;
          return v.available;
        });
        if (match) return match;
      }
    }
    return null;
  }

  // ===========================================================
  // 15. Render del Grade Picker (lista vertical de radio rows)
  //     Mock: v1-marketplace.jsx:170 — precio bold + "ahorras X€"
  // ===========================================================
  function renderGradePicker(state, productData, optionIndex) {
    var rows = document.querySelectorAll('[data-zp-grade]');
    if (!rows.length) return;
    var availability = recomputeAvailability(state, productData, optionIndex);
    var gradeAvailability = availability['Estado estético'] || {};

    rows.forEach(function (row) {
      var grade = row.getAttribute('data-zp-grade');
      var variant = priceForGrade(state, grade, productData, optionIndex);

      var priceEl   = row.querySelector('[data-zp-grade-price]');
      var savingsEl = row.querySelector('[data-zp-grade-savings]');
      var lowEl     = row.querySelector('[data-zp-grade-low]');
      var soldEl    = row.querySelector('[data-zp-grade-soldout]');

      // ¿Está agotado? Sin variante = "no existe la combinación", se trata igual que agotado.
      var isSoldOut = !variant || !variant.available;
      // Aviso + salto: si está agotado en este color pero existe en otro,
      // la fila queda clickeable y anuncia el salto ("Disponible en Dorado").
      var altVariant = isSoldOut ? altColorVariantForGrade(state, grade, productData, optionIndex) : null;
      var altColor = altVariant ? altVariant[optionIndex['Color']] : null;
      row.classList.toggle('is-sold-out', isSoldOut && !altVariant);
      row.classList.toggle('is-alt-color', !!altVariant);
      if (altColor != null) {
        row.setAttribute('data-zp-alt-color', altColor);
      } else {
        row.removeAttribute('data-zp-alt-color');
      }
      if (soldEl) soldEl.classList.toggle('zp-pdp__grade-soldout-tag--alt', !!altVariant);

      if (altVariant) {
        // Precio del color alternativo, contextualizado por el propio aviso.
        if (priceEl) priceEl.textContent = formatMoney(altVariant.price);
        if (savingsEl) savingsEl.textContent = '';
        if (lowEl) { lowEl.textContent = ''; lowEl.hidden = true; }
        if (soldEl) {
          // Reemplazo por función: un valor de color con secuencias $-especiales
          // de String.replace ($&, $$...) no corrompe el texto del aviso.
          soldEl.textContent = zpT('gradeInOtherColor', 'Disponible en [[color]]').replace('[[color]]', function () { return altColor; });
        }
      } else if (isSoldOut) {
        if (priceEl) priceEl.textContent = '';
        if (savingsEl) savingsEl.textContent = '';
        if (lowEl) { lowEl.textContent = ''; lowEl.hidden = true; }
        if (soldEl) soldEl.textContent = zpT('soldOut', 'Agotado').toUpperCase();
      } else {
        if (soldEl) soldEl.textContent = '';
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (savingsEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            var savings = variant.compare_at_price - variant.price;
            savingsEl.innerHTML = 'ahorras <strong>' + formatMoney(savings) + '</strong>';
          } else {
            savingsEl.textContent = '';
          }
        }
        if (lowEl) {
          var qty = variant.inventory_quantity;
          if (typeof qty === 'number' && qty > 0 && qty <= 5) {
            lowEl.textContent = 'ÚLTIMAS ' + qty + ' UND';
            lowEl.hidden = false;
          } else {
            lowEl.textContent = '';
            lowEl.hidden = true;
          }
        }
      }

      // Greying out adicional (combinación sin stock dado capacidad+color actuales).
      // En la práctica para grade picker is-sold-out ya cubre, pero lo dejamos por consistencia.
      var gradeAvailable = gradeAvailability[grade];
      row.classList.toggle('is-unavailable', !gradeAvailable && !isSoldOut);

      // Selected state
      var isSelected = grade === state.selectedEstadoEstetico;
      row.classList.toggle('is-selected', isSelected);
      row.setAttribute('aria-checked', String(isSelected));
      row.setAttribute('tabindex', isSelected ? '0' : '-1');
    });
  }

  // ===========================================================
  // 19. Popup "¿Qué significa cada grado?" — abrir/cerrar
  // ===========================================================
  function bindGradeHelpPopup() {
    var toggle = document.querySelector('[data-zp-grade-help-toggle]');
    var overlay = document.querySelector('[data-zp-grade-popup]');
    if (!toggle || !overlay) return;
    var closeBtn = overlay.querySelector('[data-zp-grade-popup-close]');

    function open() {
      overlay.classList.add('is-visible');
      overlay.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      overlay.classList.remove('is-visible');
      overlay.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
    toggle.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    onDocument('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-visible')) close();
    });
  }

  // ===========================================================
  // 16. Bind Grade Picker — clicks + keyboard nav (←/→ Home/End Enter/Space)
  // ===========================================================
  function bindGradePicker(state, productData, optionIndex, onChange) {
    var cards = document.querySelectorAll('[data-zp-grade]');
    if (!cards.length) return;

    bindGradeHelpPopup();

    function selectGradeCard(card) {
      // Selección de una fila de grado, compartida por click y teclado.
      // - Agotado sin alternativa: nudge (en móvil no se ve el not-allowed).
      // - "Disponible en [color]": salto anunciado → cambia color + grado a
      //   una combinación comprable (la fila ya muestra ese precio).
      // - Normal: selecciona el grado.
      if (card.classList.contains('is-sold-out')) {
        card.classList.remove('zp-grade-nudge');
        void card.offsetWidth; // reinicia la animación
        card.classList.add('zp-grade-nudge');
        return;
      }
      var grade = card.getAttribute('data-zp-grade');
      var altColor = card.getAttribute('data-zp-alt-color');
      if (altColor != null && altColor !== '') {
        state.selectedColor = altColor;
        state.selectedEstadoEstetico = grade;
        onChange();
        return;
      }
      if (grade === state.selectedEstadoEstetico) return;
      state.selectedEstadoEstetico = grade;
      onChange();
    }

    cards.forEach(function (card, i) {
      card.addEventListener('click', function () {
        selectGradeCard(card);
      });

      card.addEventListener('keydown', function (e) {
        var nextIdx = null;
        switch (e.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            nextIdx = (i + 1) % cards.length;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            nextIdx = (i - 1 + cards.length) % cards.length;
            break;
          case 'Home':
            nextIdx = 0;
            break;
          case 'End':
            nextIdx = cards.length - 1;
            break;
          case ' ':
          case 'Enter':
            e.preventDefault();
            selectGradeCard(card);
            return;
          default:
            return;
        }
        e.preventDefault();
        var nextCard = cards[nextIdx];
        // Las flechas mueven el foco; solo seleccionan si la fila destino es
        // seleccionable directa (ni agotada ni de salto). El salto de color
        // queda para Enter/Espacio: cambiar de color con una flecha sería
        // desorientador (cambia foto y precio sin confirmación).
        if (!nextCard.classList.contains('is-sold-out') && !nextCard.classList.contains('is-alt-color')) {
          var nextGrade = nextCard.getAttribute('data-zp-grade');
          if (nextGrade !== state.selectedEstadoEstetico) {
            state.selectedEstadoEstetico = nextGrade;
            onChange();
          }
        }
        nextCard.focus();
      });
    });
  }

  // ===========================================================
  // 17. Galería — bind thumbs (click → swap main image + counter)
  //
  // Además del swap visual, si la foto clicada corresponde a un COLOR
  // (marketplace-style: "elijo el color tocando su foto"), seleccionamos
  // esa variante de color. Es el enlace inverso de syncGalleryToVariant
  // (color → foto): aquí hacemos foto → color. Reutiliza el mismo matching
  // por basename de imagen que ya usa syncGalleryToVariant (#23).
  // ===========================================================
  function buildSlideColorMap(productData, optionIndex) {
    // slideIndex (número) -> valor de color
    var map = {};
    if (!productData || !productData.variants || !optionIndex['Color']) return map;
    var colorKey = optionIndex['Color'];

    // basename de la featured image -> color (primer color que la reclama)
    var basenameColor = {};
    productData.variants.forEach(function (v) {
      var src = null;
      if (v.featured_image && v.featured_image.src) {
        src = v.featured_image.src;
      } else if (v.featured_media && v.featured_media.preview_image && v.featured_media.preview_image.src) {
        src = v.featured_media.preview_image.src;
      }
      if (!src) return;
      var bn = imageBasename(src);
      var color = v[colorKey];
      if (bn && color && basenameColor[bn] == null) basenameColor[bn] = color;
    });

    document.querySelectorAll('[data-zp-slide]').forEach(function (s) {
      var img = s.querySelector('img');
      if (!img) return;
      var bn = imageBasename(img.getAttribute('src'));
      var idx = parseInt(s.getAttribute('data-zp-slide-index'), 10);
      if (!isNaN(idx) && basenameColor[bn] != null) map[idx] = basenameColor[bn];
    });
    return map;
  }

  function bindGalleryThumbs(state, productData, optionIndex, onChange) {
    var thumbs = document.querySelectorAll('[data-zp-thumb]');
    var slides = document.querySelectorAll('[data-zp-slide]');
    var currentEl = document.querySelector('[data-zp-gallery-current]');
    if (!thumbs.length || !slides.length) return;

    var slideColor = buildSlideColorMap(productData, optionIndex);

    function activate(index) {
      thumbs.forEach(function (t) {
        var i = parseInt(t.getAttribute('data-zp-thumb-index'), 10);
        var active = i === index;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', String(active));
      });
      slides.forEach(function (s) {
        var i = parseInt(s.getAttribute('data-zp-slide-index'), 10);
        s.classList.toggle('is-active', i === index);
      });
      if (currentEl) currentEl.textContent = String(index + 1);
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var idx = parseInt(thumb.getAttribute('data-zp-thumb-index'), 10);
        if (isNaN(idx)) return;

        // Swap visual inmediato (cubre también slides sin color: videos, tomas compartidas)
        activate(idx);

        // Si esta foto representa un color y el estado lo tiene cableado,
        // seleccionar ese color (foto → color). onChange re-renderiza swatches,
        // buy box, SKU, precio y re-sincroniza la galería.
        if (state && typeof onChange === 'function') {
          var color = slideColor[idx];
          if (color != null && color !== state.selectedColor) {
            state.selectedColor = color;
            // Mismo auto-salto que el clic en el swatch: aterrizar comprable.
            snapToAvailable(state, 'Color', productData, optionIndex);
            onChange();
          }
        }
      });
    });
  }

  // ===========================================================
  // 18. Render del pill "Grado: X" en la galería al cambiar variante
  // ===========================================================
  function renderGradePill(state) {
    var pill = document.querySelector('[data-zp-grade-pill]');
    if (!pill) return;
    // El pill es el <strong> del valor; el botón que lo contiene lleva la
    // etiqueta traducida ("Estado:") y el "Cambiar".
    var holder = pill.closest('[data-zp-grade-jump]') || pill;
    if (state.selectedEstadoEstetico) {
      pill.textContent = state.selectedEstadoEstetico;
      holder.hidden = false;
    } else {
      holder.hidden = true;
    }
  }

  // El botón del estado en la galería lleva al selector de estado y le da el
  // foco a la opción elegida (o a la primera, si no hay ninguna marcada).
  function jumpToGradePicker() {
    var picker = document.querySelector('.zp-pdp__grade-picker');
    if (!picker) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    picker.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    var target = picker.querySelector('[data-zp-grade][aria-checked="true"]') || picker.querySelector('[data-zp-grade]');
    if (target) target.focus({ preventScroll: true });
  }

  // ===========================================================
  // 20. Quick Info — render selección Color + Capacidad (con disponibilidad)
  // ===========================================================
  function renderQuickInfo(state, productData, optionIndex) {
    var availability = recomputeAvailability(state, productData, optionIndex);

    // Color swatches
    var swatches = document.querySelectorAll('[data-zp-color]');
    var colorAvail = availability['Color'] || {};
    swatches.forEach(function (sw) {
      var val = sw.getAttribute('data-zp-color');
      var isSelected = val === state.selectedColor;
      var isAvailable = colorAvail[val] !== false;
      sw.classList.toggle('is-selected', isSelected);
      sw.classList.toggle('is-unavailable', !isAvailable);
      sw.setAttribute('aria-checked', String(isSelected));
      sw.setAttribute('tabindex', isSelected ? '0' : '-1');
    });

    // Label del color seleccionado
    var colorLabel = document.querySelector('[data-zp-color-label]');
    if (colorLabel) colorLabel.textContent = state.selectedColor || '';

    // Capacity chips
    var caps = document.querySelectorAll('[data-zp-storage]');
    var storageAvail = availability['Almacenamiento'] || {};
    if (!caps.length) return;

    // Precio base = la capacidad más chica disponible con el resto del estado actual
    var baseCapacityPrice = null;
    var basePerCap = {}; // capacidad -> price (variante con grado y color actuales, o fallback a cualquier variante con esa capacidad)

    caps.forEach(function (chip) {
      var val = chip.getAttribute('data-zp-storage');
      // Intentar match exacto con grado + color actuales
      var match = productData.variants.find(function (v) {
        return v[optionIndex['Almacenamiento']] === val
            && v[optionIndex['Estado estético']] === state.selectedEstadoEstetico
            && v[optionIndex['Color']] === state.selectedColor;
      });
      // Fallback: cualquier variante con esa capacidad
      if (!match) {
        match = productData.variants.find(function (v) {
          return v[optionIndex['Almacenamiento']] === val;
        });
      }
      if (match) basePerCap[val] = match.price;
    });

    // Determinar capacidad base (la más chica numéricamente)
    var capValues = Array.from(caps).map(function (c) { return c.getAttribute('data-zp-storage'); });
    var sortedByGB = capValues.slice().sort(function (a, b) {
      return parseInt(a, 10) - parseInt(b, 10);
    });
    var baseCap = sortedByGB[0];
    var basePrice = basePerCap[baseCap];

    caps.forEach(function (chip) {
      var val = chip.getAttribute('data-zp-storage');
      var isSelected = val === state.selectedAlmacenamiento;
      var isAvailable = storageAvail[val] !== false;
      // Aviso + salto (mismo patrón que el grade picker): capacidad agotada
      // en el color actual pero disponible en otro → chip clickeable que
      // anuncia "en otro color" en vez de tacharse.
      var altVariant = isAvailable ? null : altVariantForStorage(state, val, productData, optionIndex);
      var altColor = altVariant ? altVariant[optionIndex['Color']] : null;
      chip.classList.toggle('is-selected', isSelected);
      chip.classList.toggle('is-unavailable', !isAvailable && !altVariant);
      chip.classList.toggle('is-alt-color', !!altVariant);
      if (altColor != null) {
        chip.setAttribute('data-zp-alt-color', altColor);
        chip.setAttribute('data-zp-alt-grade', optionIndex['Estado estético'] ? altVariant[optionIndex['Estado estético']] : '');
      } else {
        chip.removeAttribute('data-zp-alt-color');
        chip.removeAttribute('data-zp-alt-grade');
      }
      chip.setAttribute('aria-checked', String(isSelected));
      chip.setAttribute('tabindex', isSelected ? '0' : '-1');

      var deltaEl = chip.querySelector('[data-zp-capacity-delta]');
      if (deltaEl) {
        if (altVariant) {
          // El aviso ocupa el lugar del delta de precio: es la información
          // que decide el clic; el precio exacto se ve tras el salto.
          deltaEl.textContent = zpT('capacityOtherColor', 'en otro color');
        } else {
          var price = basePerCap[val];
          if (price == null || basePrice == null) {
            deltaEl.textContent = '';
          } else if (val === baseCap) {
            deltaEl.textContent = 'estándar';
          } else {
            var delta = price - basePrice;
            if (delta === 0) {
              deltaEl.textContent = 'estándar';
            } else if (delta > 0) {
              deltaEl.textContent = '+' + formatMoney(delta);
            } else {
              deltaEl.textContent = formatMoney(delta);
            }
          }
        }
      }
    });
  }

  function bindQuickInfo(state, productData, optionIndex, onChange) {
    document.querySelectorAll('[data-zp-color]').forEach(function (sw) {
      sw.addEventListener('click', function () {
        var val = sw.getAttribute('data-zp-color');
        if (val === state.selectedColor) return;
        state.selectedColor = val;
        // Si el grado/capacidad actuales no existen en este color, saltar
        // a la mejor combinación disponible (nunca dejar "Sin stock" mudo).
        snapToAvailable(state, 'Color', productData, optionIndex);
        onChange();
      });
    });
    document.querySelectorAll('[data-zp-storage]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var val = chip.getAttribute('data-zp-storage');
        var altColor = chip.getAttribute('data-zp-alt-color');
        if (altColor != null && altColor !== '') {
          // Salto anunciado ("en otro color"): esta capacidad no existe en el
          // color actual — cambiar también color (y grado) para aterrizar en
          // la combinación comprable que el propio chip anuncia.
          state.selectedAlmacenamiento = val;
          state.selectedColor = altColor;
          var altGrade = chip.getAttribute('data-zp-alt-grade');
          if (altGrade) state.selectedEstadoEstetico = altGrade;
          snapToAvailable(state, 'Almacenamiento', productData, optionIndex);
          onChange();
          return;
        }
        if (val === state.selectedAlmacenamiento) return;
        state.selectedAlmacenamiento = val;
        snapToAvailable(state, 'Almacenamiento', productData, optionIndex);
        onChange();
      });
    });

    // Scroll smooth a la sección de reseñas Judge.me cuando se clickea el badge
    var reviewsLink = document.querySelector('[data-zp-reviews-anchor]');
    if (reviewsLink) {
      reviewsLink.addEventListener('click', function (e) {
        // Buscar el widget real de Judge.me en la página (su clase estándar)
        var target = document.querySelector('.jdgm-rev-widg, .jdgm-widget--full-rev-widg, #judge-me-reviews');
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  }

  // ===========================================================
  // 21b. PayPal Pay Later Messaging — actualizar amount al cambiar variante
  // ===========================================================
  function updatePaypalMessage(variant) {
    var msgEl = document.querySelector('[data-zp-paypal-msg]');
    if (!msgEl || !variant) return;
    var price = (variant.price / 100).toFixed(2);
    msgEl.setAttribute('data-pp-amount', price);
    if (window.paypal && window.paypal.Messages) {
      try {
        window.paypal.Messages({ amount: parseFloat(price) }).render(msgEl);
      } catch (e) { /* SDK no cargado aún, se renderiza solo en el siguiente load */ }
    }
  }
  document.addEventListener('zp:variant-change', function (e) {
    if (e && e.detail && e.detail.variant) updatePaypalMessage(e.detail.variant);
  });

  // Initial render del PayPal Messages: a veces el SDK no auto-renderiza
  // si carga después del DOMContentLoaded. Forzamos el render con polling
  // suave hasta que window.paypal.Messages esté disponible o pasen 5s.
  //
  // Además, si el SDK acepta el render pero PayPal decide NO mostrar el
  // widget (típico cuando el monto no es elegible para Pay Later en este
  // merchant — Promise pending forever sin iframe), ocultamos el wrapper
  // para no dejar espacio en blanco en el PDP.
  (function forcePaypalInitialRender() {
    var attempts = 0;
    var maxAttempts = 25; // 25 × 200ms = 5s buscando SDK
    function checkRendered() {
      var msgEl = document.querySelector('[data-zp-paypal-msg]');
      if (!msgEl) return;
      // Si después de 3s no hay iframe, asumimos que el monto no es elegible
      // y ocultamos el wrapper para mantener el PDP limpio.
      setTimeout(function () {
        if (!msgEl.querySelector('iframe')) {
          var wrapper = msgEl.closest('.zp-pdp__qi-paypal');
          if (wrapper) wrapper.style.display = 'none';
        }
      }, 3000);
    }
    function tryRender() {
      var msgEl = document.querySelector('[data-zp-paypal-msg]');
      if (!msgEl) return;
      var amount = parseFloat(msgEl.getAttribute('data-pp-amount'));
      if (!amount) return;
      if (window.paypal && window.paypal.Messages) {
        try { window.paypal.Messages({ amount: amount }).render(msgEl); } catch (e) {}
        checkRendered();
        return;
      }
      if (++attempts < maxAttempts) setTimeout(tryRender, 200);
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', tryRender);
    } else {
      tryRender();
    }
  })();

  // ===========================================================
  // 21c. Sequra Promotion Widget — actualizar amount al cambiar variante
  //      El script de Sequra appendea un .sequra-promotion-widget dentro
  //      del anchor .product-form__variants (data-zp-sequra-anchor).
  //      Replicamos lo que hace theme.js:10889 al cambiar variante.
  // ===========================================================
  function updateSequraWidget(variant) {
    var anchor = document.querySelector('[data-zp-sequra-anchor]');
    if (!anchor || !variant) return;
    var widget = anchor.querySelector('.sequra-promotion-widget');
    if (!widget) return;
    widget.setAttribute('data-amount', variant.price);
    var iframe = widget.querySelector('iframe');
    if (iframe && iframe.src) {
      // Reasignar el mismo src recarga el iframe y lo deja parpadeando (o en
      // blanco) sin motivo: solo lo tocamos si el importe cambió de verdad.
      var nextSrc = iframe.src.replace(/totalWithTax=\d+/, 'totalWithTax=' + variant.price);
      if (nextSrc !== iframe.src) iframe.src = nextSrc;
    }
    if (window.Sequra && typeof window.Sequra.refreshComponents === 'function') {
      try { window.Sequra.refreshComponents(); } catch (e) { /* SDK aún no listo */ }
    }
  }
  document.addEventListener('zp:variant-change', function (e) {
    if (e && e.detail && e.detail.variant) updateSequraWidget(e.detail.variant);
  });

  // ===========================================================
  // 22. Battery Addon — match por modelo de iPhone, ocultar si Premium
  // ===========================================================
  function extractIPhoneModel(title) {
    var match = title.match(/iPhone\s*\d+\s*(Pro\s*Max|Pro|Plus|Mini)?/i);
    return match ? match[0].trim() : '';
  }

  function bindBatteryAddon(state) {
    var addon = document.querySelector('[data-zp-battery]');
    if (!addon) return;

    var handle = addon.getAttribute('data-zp-battery-handle') || 'remplazo-de-bateria';
    var hideForGrade = addon.getAttribute('data-zp-battery-hide-grade') || 'Premium';
    var productTitle = addon.getAttribute('data-product-title') || '';
    var priceEl = addon.querySelector('[data-zp-battery-price]');
    var compareEl = addon.querySelector('[data-zp-battery-compare]');
    var badgeEl = addon.querySelector('[data-zp-battery-badge]');
    var badgeDefault = badgeEl ? badgeEl.textContent.trim() : '';
    var checkbox = addon.querySelector('[data-zp-battery-input]');

    var batteryVariants = [];
    var batteryVariantId = null;

    var currentModel = extractIPhoneModel(productTitle);

    // El descuento del add-on se hace con precio real + compare_at_price en las
    // variantes de batería (no con descuentos automáticos: los de "Accesorios
    // -33% con iPhone" tienen combinesWith.productDiscounts=false y no
    // combinarían). El % se CALCULA aquí en vez de guardarse como ajuste del
    // bloque: los ajustes son traducibles y Translate & Adapt ya rompió este
    // add-on en /pt. Sin compare_at_price la fila queda como antes.
    function renderPrice(price, compareAt) {
      if (priceEl) priceEl.textContent = formatMoney(price);

      // Un -0% (diferencia de céntimos) no es una oferta: se trata como si no
      // hubiera descuento, así tachado y badge nunca se contradicen.
      var pct = (typeof compareAt === 'number' && compareAt > price)
        ? Math.floor((compareAt - price) * 100 / compareAt)
        : 0;
      var hasDiscount = pct > 0;

      if (compareEl) {
        compareEl.textContent = hasDiscount ? formatMoney(compareAt) : '';
        compareEl.hidden = !hasDiscount;
      }
      if (badgeEl) {
        badgeEl.textContent = hasDiscount ? ('-' + pct + '%') : badgeDefault;
        badgeEl.classList.toggle('is-discount', hasDiscount);
      }
    }

    function matchVariant() {
      if (!currentModel || batteryVariants.length === 0) return false;
      var modelLower = currentModel.toLowerCase().replace(/\s+/g, ' ').trim();
      var sorted = batteryVariants.slice().sort(function (a, b) { return b.title.length - a.title.length; });
      for (var i = 0; i < sorted.length; i++) {
        var varTitle = sorted[i].title.toLowerCase().replace(/\s+/g, ' ').trim();
        if (varTitle === modelLower || modelLower.indexOf(varTitle) !== -1) {
          // Si la batería para este iPhone está agotada, NO mostrar el addon.
          // Sino el usuario marca el checkbox y al "Añadir al carrito" Shopify
          // rechaza con "está agotado".
          if (sorted[i].available === false) return false;
          batteryVariantId = sorted[i].id;
          window.__zpBatteryVariantId = batteryVariantId;
          state.batteryPrice = parseInt(sorted[i].price, 10) || 0;
          var compareAt = parseInt(sorted[i].compare_at_price, 10);
          renderPrice(state.batteryPrice, isNaN(compareAt) ? null : compareAt);
          return true;
        }
      }
      return false;
    }

    // El cambio de batería se hace en el taller antes de enviar, así que el
    // pedido no sale en 24/48h. El aviso de plazo acompaña al checkbox: visible
    // solo si el addon está visible Y marcado.
    var etaEl = document.querySelector('[data-zp-battery-eta]');
    function updateEta() {
      if (!etaEl) return;
      etaEl.hidden = !(checkbox && checkbox.checked && !addon.hidden);
    }

    function updateVisibility() {
      if (!batteryVariantId) { addon.hidden = true; updateEta(); return; }
      if (state.selectedEstadoEstetico === hideForGrade) {
        addon.hidden = true;
        if (checkbox && checkbox.checked) {
          checkbox.checked = false;
          state.addBattery = false;
          addon.classList.remove('is-checked');
          window.__zpBatteryVariantId = null;
        }
      } else {
        addon.hidden = false;
        window.__zpBatteryVariantId = batteryVariantId;
      }
      updateEta();
    }

    // Prefijo de mercado (/pt, /fr...): sin él, en /pt se pedían los precios
    // del mercado español. routes.root_url lo emite Liquid en el data-attr.
    var root = addon.getAttribute('data-zp-battery-root') || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';

    fetch(root + 'products/' + handle + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('battery product not found: ' + r.status);
        return r.json();
      })
      .then(function (data) {
        batteryVariants = data.variants || [];
        if (matchVariant()) updateVisibility();
      })
      .catch(function () { /* producto no existe — addon queda oculto */ });

    if (checkbox) {
      checkbox.addEventListener('change', function () {
        state.addBattery = checkbox.checked;
        addon.classList.toggle('is-checked', checkbox.checked);
        updateEta();
        // Notificar que el total cambió (precio + savings + financiación)
        document.dispatchEvent(new CustomEvent('zp:battery-toggle', { detail: { state: state } }));
      });
    }

    // Re-evaluar visibilidad cada vez que cambia la variante (escucha el evento global)
    onDocument('zp:variant-change', updateVisibility);
  }

  // ===========================================================
  // 23. Sync galería con variante (color → imagen principal)
  // ===========================================================
  function imageBasename(url) {
    if (!url) return '';
    var noQuery = String(url).split('?')[0];
    var parts = noQuery.split('/');
    return parts[parts.length - 1].toLowerCase();
  }

  function activateGallerySlide(idx) {
    var thumbs = document.querySelectorAll('[data-zp-thumb]');
    var slides = document.querySelectorAll('[data-zp-slide]');
    var currentEl = document.querySelector('[data-zp-gallery-current]');
    thumbs.forEach(function (t) {
      var i = parseInt(t.getAttribute('data-zp-thumb-index'), 10);
      var active = i === idx;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-selected', String(active));
    });
    slides.forEach(function (s) {
      var i = parseInt(s.getAttribute('data-zp-slide-index'), 10);
      s.classList.toggle('is-active', i === idx);
    });
    if (currentEl) currentEl.textContent = String(idx + 1);
  }

  function syncGalleryToVariant(variant) {
    if (!variant) return;
    // Shopify variant JSON expone featured_image (estándar) o featured_media
    var src = null;
    if (variant.featured_image && variant.featured_image.src) {
      src = variant.featured_image.src;
    } else if (variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src) {
      src = variant.featured_media.preview_image.src;
    }
    if (!src) return;
    var target = imageBasename(src);
    var slides = document.querySelectorAll('[data-zp-slide]');
    for (var i = 0; i < slides.length; i++) {
      var img = slides[i].querySelector('img');
      if (img && imageBasename(img.getAttribute('src')) === target) {
        var idx = parseInt(slides[i].getAttribute('data-zp-slide-index'), 10);
        if (!isNaN(idx)) activateGallerySlide(idx);
        return;
      }
    }
  }
  // ===========================================================
  // 24. Ficha unificada SIM/eSIM — selector "Tipo de SIM" + swap sin recarga
  //     Spec: docs/superpowers/specs/2026-09-16-ficha-unificada-sim-esim-design.md
  //     El gemelo llega en productData.twin con la misma forma que productData
  //     (variants con option1..3 + options_with_values), así reutilizamos
  //     buildOptionIndex / optionValuesForAxis / preferCurrentFirst.
  // ===========================================================

  // Igualdad de valores de opción ENTRE productos: sin acentos ni espacios
  // ('512 GB' == '512GB', 'Púrpura' == 'PURPURA'). Translate & Adapt puede
  // haber tocado el gemelo de forma distinta al producto actual.
  function sameVal(a, b) {
    if (a == null || b == null) return a == b;
    return normalizarValor(a).replace(/\s+/g, '') === normalizarValor(b).replace(/\s+/g, '');
  }

  // Primera variante DISPONIBLE del gemelo que casa con los ejes dados
  // (null en un eje = cualquiera). El orden de variants es el de display.
  function twinFind(twin, idx, color, storage, grade) {
    return twin.variants.find(function (v) {
      if (color   != null && idx['Color']           && !sameVal(v[idx['Color']], color)) return false;
      if (storage != null && idx['Almacenamiento']  && !sameVal(v[idx['Almacenamiento']], storage)) return false;
      if (grade   != null && idx['Estado estético'] && !sameVal(v[idx['Estado estético']], grade)) return false;
      return !!v.available;
    }) || null;
  }

  // Variante del gemelo para la selección actual → { variant, exact } o null.
  // Orden (mismo criterio que el picker, PR #152/#153): exacta → mismo color y
  // capacidad con otro grado → misma capacidad en otro color (grado actual
  // primero) → cualquiera disponible.
  function twinVariantFor(state, twin) {
    if (!twin || !twin.variants || !twin.variants.length) return null;
    var idx = buildOptionIndex(twin);
    var exact = twinFind(twin, idx, state.selectedColor, state.selectedAlmacenamiento, state.selectedEstadoEstetico);
    if (exact) return { variant: exact, exact: true };
    var byGrade = twinFind(twin, idx, state.selectedColor, state.selectedAlmacenamiento, null);
    if (byGrade) return { variant: byGrade, exact: false };
    var grades = preferCurrentFirst(state.selectedEstadoEstetico, optionValuesForAxis('Estado estético', twin, idx));
    if (!grades.length) grades = [null];
    for (var g = 0; g < grades.length; g++) {
      var byColor = twinFind(twin, idx, null, state.selectedAlmacenamiento, grades[g]);
      if (byColor) return { variant: byColor, exact: false };
    }
    var any = twinFind(twin, idx, null, null, null);
    return any ? { variant: any, exact: false } : null;
  }

  // ¿Podemos cambiar de ficha sin recargar? Requiere gemelo, APIs del
  // navegador, MISMA plantilla (el section_id depende del fichero de
  // plantilla) y la sección montada dentro de su wrapper .shopify-section.
  function canSwapInPlace() {
    var twin = productData && productData.twin;
    if (!twin || !twin.url) return false;
    if (!window.fetch || !window.history || !window.DOMParser) return false;
    var cur = (productData.product && productData.product.templateSuffix) || '';
    if ((twin.templateSuffix || '') !== cur) return false;
    var section = document.querySelector('.zp-pdp[data-section-id]');
    return !!(section && section.closest && section.closest('.shopify-section'));
  }
  // Pinta la tarjeta del gemelo para la selección actual (y el precio de la
  // tarjeta marcada). Se llama en el boot y en cada onSelectionChange.
  function renderSimSelector(state, currentVariant) {
    var root = document.querySelector('[data-zp-sim]');
    if (!root || !productData) return;
    var curPrice = root.querySelector('[data-zp-sim-current-price]');
    if (curPrice && currentVariant) {
      var anyAvailable = productData.variants.some(function (v) { return v.available; });
      curPrice.textContent = anyAvailable ? formatMoney(currentVariant.price) : zpT('simSoldOut', 'Agotado');
    }

    var card = root.querySelector('[data-zp-sim-twin]');
    if (!card) return;
    var priceEl = card.querySelector('[data-zp-sim-twin-price]');
    var tagEl = card.querySelector('[data-zp-sim-twin-tag]');
    var twin = productData.twin;
    var res = twinVariantFor(state, twin);

    card.classList.remove('is-alt', 'is-sold-out');
    if (tagEl) {
      tagEl.textContent = '';
      tagEl.hidden = true;
      tagEl.classList.remove('zp-pdp__sim-tag--save', 'zp-pdp__sim-tag--alt');
    }

    if (!res) {
      card.classList.add('is-sold-out');
      card.removeAttribute('href');
      card.setAttribute('aria-disabled', 'true');
      if (priceEl) priceEl.textContent = zpT('simSoldOut', 'Agotado');
      return;
    }

    card.setAttribute('href', twin.url + '?variant=' + res.variant.id);
    card.removeAttribute('aria-disabled');
    if (priceEl) {
      priceEl.textContent = formatMoney(res.variant.price) + (res.variant.preorder ? ' · ' + zpT('simPreorder', 'Reserva') : '');
    }
    if (res.exact) {
      var saving = currentVariant ? currentVariant.price - res.variant.price : 0;
      if (saving > 0 && tagEl) {
        tagEl.textContent = zpT('simSave', 'Ahorra') + ' ' + formatMoney(saving);
        tagEl.classList.add('zp-pdp__sim-tag--save');
        tagEl.hidden = false;
      }
    } else {
      card.classList.add('is-alt');
      if (tagEl) {
        var idx = buildOptionIndex(twin);
        var parts = [];
        if (idx['Color'] && res.variant[idx['Color']]) parts.push(res.variant[idx['Color']]);
        if (idx['Estado estético'] && res.variant[idx['Estado estético']]) parts.push(res.variant[idx['Estado estético']]);
        tagEl.textContent = zpT('simInOther', 'Disponible en') + ' ' + parts.join(' · ');
        tagEl.classList.add('zp-pdp__sim-tag--alt');
        tagEl.hidden = false;
      }
    }
  }

  // Tras un swap, "Atrás"/"Adelante" cambian la URL a otra ficha: recargar
  // es lo simple y correcto. El picker usa replaceState, así que popstate solo
  // se dispara por nuestros pushState.
  var zpSwapped = false;
  window.addEventListener('popstate', function () {
    if (zpSwapped) window.location.reload();
  });

  // Swap en vuelo. Si el usuario toca el picker mientras carga, la respuesta
  // pendiente sería la del href VIEJO y además renderBuyBox ya habría
  // reactivado las CTAs: se aborta y se limpia el estado visual de la tarjeta.
  var zpSwapController = null;
  function cancelSwap() {
    if (zpSwapController) {
      zpSwapController.abort();
      zpSwapController = null;
    }
    var loading = document.querySelector('[data-zp-sim-twin].is-loading');
    if (loading) {
      loading.classList.remove('is-loading');
      loading.removeAttribute('aria-busy');
    }
    // Autocontenido: quien cancele no depende de que luego se repinte el buy box
    // (renderBuyBox vuelve a fijar el estado real de los CTAs de todos modos).
    setCtasDisabled(false);
  }

  function setCtasDisabled(disabled) {
    document.querySelectorAll('[data-zp-add-to-cart],[data-zp-buy-now],[data-zp-sticky-cta]').forEach(function (b) {
      b.disabled = disabled;
    });
  }

  // Pide la sección PDP del gemelo (Section Rendering API) y la mete en el
  // sitio de la actual. Cualquier fallo → navegación normal al href.
  // opts.auto = el cambio lo decidimos nosotros al aterrizar en una ficha
  // agotada, no lo pidió el usuario. Eso cambia tres cosas: se reemplaza la
  // entrada del historial en vez de apilarla (Atrás debe volver al listado, no
  // a la ficha sin stock), no se roba el foco, y un fallo NO navega.
  function swapToTwin(href, card, opts) {
    var auto = !!(opts && opts.auto);
    if (card.classList.contains('is-loading')) return; // doble clic: ya está en marcha
    var section = document.querySelector('.zp-pdp[data-section-id]');
    var wrapper = section && section.closest('.shopify-section');
    if (!wrapper) { window.location.href = href; return; }
    var sectionId = section.getAttribute('data-section-id');
    var url = href + (href.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId);
    var fromTitle = productData.product.title;
    var fromHandle = productData.product.handle;
    var twinTitle = productData.twin.title;
    var scrollY = window.scrollY;

    cancelSwap();
    zpSwapController = window.AbortController ? new AbortController() : null;

    card.classList.add('is-loading');
    card.setAttribute('aria-busy', 'true');
    setCtasDisabled(true);

    fetch(url, {
      credentials: 'same-origin',
      headers: { 'Accept': 'text/html' },
      signal: zpSwapController ? zpSwapController.signal : undefined
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var data = doc.getElementById('zp-product-data');
        var newWrapper = data && data.closest('.shopify-section');
        if (!newWrapper) throw new Error('la sección recibida no trae #zp-product-data');
        // Un redirect de idioma (a /pt, por ejemplo) devuelve 200 con OTRA
        // ficha: comprobamos que lo que llega es de verdad el gemelo.
        var fetched;
        try { fetched = JSON.parse(data.textContent); } catch (e) { fetched = null; }
        if (!fetched || !fetched.product || fetched.product.url !== productData.twin.url) {
          throw new Error('la sección recibida no es la del gemelo');
        }
        // El widget de SeQura lo inyecta su propio script una sola vez; si lo
        // barre el innerHTML, updateSequraWidget no vuelve a encontrarlo nunca.
        // Lo movemos al anchor nuevo (igual que hace theme.js).
        // SeQura inyecta su widget en [data-zp-sequra-anchor] al cargar la página y
        // el innerHTML de abajo lo destruye. Ni mover ni clonar el nodo sirven: el
        // iframe se renavega al cambiar de padre y acaba con altura 0 (SeQura ya no
        // lo reconoce como suyo). Lo que sí funciona es devolverle un contenedor
        // VACÍO con sus data-* y SIN data-loaded, y pedirle que lo renderice: crea
        // un iframe nuevo y le ajusta la altura como en una carga normal.
        var sequraOld = wrapper.querySelector('.sequra-promotion-widget');
        var sequraAttrs = null;
        if (sequraOld) {
          sequraAttrs = [];
          Array.prototype.forEach.call(sequraOld.attributes, function (a) {
            if (a.name !== 'data-loaded' && a.name !== 'style') sequraAttrs.push([a.name, a.value]);
          });
        }
        wrapper.innerHTML = newWrapper.innerHTML;
        var sequraAnchor = wrapper.querySelector('[data-zp-sequra-anchor]');
        if (sequraAnchor && sequraAttrs && !sequraAnchor.querySelector('.sequra-promotion-widget')) {
          var sequraEl = document.createElement('div');
          for (var si = 0; si < sequraAttrs.length; si++) {
            sequraEl.setAttribute(sequraAttrs[si][0], sequraAttrs[si][1]);
          }
          var sequraVariant = pickInitialVariant(productData);
          if (sequraVariant && sequraVariant.price != null) {
            sequraEl.setAttribute('data-amount', sequraVariant.price);
          }
          sequraAnchor.appendChild(sequraEl);
          if (window.Sequra && typeof window.Sequra.refreshComponents === 'function') {
            try { window.Sequra.refreshComponents(); } catch (e) { /* SDK aún no listo */ }
          }
        }
        zpSwapController = null;
        zpSwapped = true;
        if (auto) {
          history.replaceState({ zpSwap: true }, '', href);
        } else {
          history.pushState({ zpSwap: true }, '', href);
        }
        if (fromTitle && twinTitle && document.title.indexOf(fromTitle) !== -1) {
          // split/join: replace() interpretaría $& y compañía en el título.
          document.title = document.title.split(fromTitle).join(twinTitle);
        }
        bootPdp();
        // Los handlers de SeQura y PayPal viven a nivel módulo y solo reaccionan
        // a zp:variant-change; el boot no lo emite (lo emitiría dos veces en una
        // carga normal), así que aquí lo lanzamos una vez.
        document.dispatchEvent(new CustomEvent('zp:variant-change', {
          detail: { variant: pickInitialVariant(productData) }
        }));
        window.scrollTo(0, scrollY);
        // El innerHTML destruyó el enlace que tenía el foco: lo movemos a la tarjeta
        // ahora marcada para que teclado y lector de pantalla sepan dónde están.
        // En el cambio automático no: nadie tenía el foco ahí (pasa durante la
        // carga) y moverlo desorienta.
        if (!auto) {
          var selectedCard = wrapper.querySelector('[data-zp-sim-current]');
          if (selectedCard && typeof selectedCard.focus === 'function') {
            try { selectedCard.focus({ preventScroll: true }); } catch (e) { selectedCard.focus(); }
          }
        }
        // Sin page_view manual: Enhanced Measurement de GA4 ya lo dispara con
        // pushState. zp:product-swap queda para instrumentación propia.
        document.dispatchEvent(new CustomEvent('zp:product-swap', {
          detail: { from: fromHandle, to: productData.product.handle, variant: pickInitialVariant(productData), url: window.location.href, auto: auto }
        }));
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return; // cancelado adrede
        // Un cambio que el usuario no pidió no puede acabar en una navegación a
        // pelo: se deja la ficha como estaba (el selector ya enlaza al gemelo).
        if (auto) {
          console.warn('[zp-pdp] cambio automático a la versión con stock falló:', err && err.message);
          cancelSwap();
          return;
        }
        console.warn('[zp-pdp] swap SIM/eSIM falló, navegando:', err && err.message);
        window.location.href = href;
      });
  }

  // ¿Aterrizamos en una ficha sin NADA que vender teniendo el gemelo con stock?
  // Mira solo inventario; las guardas de DOM y de navegador van en el llamador.
  // Una variante en reserva cuenta como disponible: eso sí se puede comprar.
  function shouldAutoSwap(data) {
    if (!data || !data.variants || !data.variants.length) return false;
    if (!data.twin || !data.twin.variants || !data.twin.variants.length) return false;
    if (data.variants.some(function (v) { return v.available; })) return false;
    return data.twin.variants.some(function (v) { return v.available; });
  }

  // Un intento por carga de página, nunca después de un cambio (ni automático
  // ni del usuario): si no, dos fichas agotadas se rebotarían la una a la otra.
  var zpAutoSwapTried = false;
  function maybeAutoSwapToTwin() {
    if (zpAutoSwapTried || zpSwapped) return;
    zpAutoSwapTried = true;
    if (!shouldAutoSwap(productData)) return;
    var card = document.querySelector('[data-zp-sim-twin]');
    if (!card) return;
    var href = card.getAttribute('href');
    if (!href || card.getAttribute('aria-disabled') === 'true') return;
    // Sin swap en vuelo no redirigimos: una navegación automática al cargar es
    // peor que quedarse (parpadeo, historial roto) y el selector ya enseña que
    // la otra versión tiene stock.
    if (!canSwapInPlace()) return;
    swapToTwin(href, card, { auto: true });
  }

  function bindSimSelector() {
    var card = document.querySelector('[data-zp-sim-twin]');
    if (!card) return;
    card.addEventListener('click', function (e) {
      var href = card.getAttribute('href');
      if (!href || card.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; // abrir en pestaña nueva, etc.
      if (!canSwapInPlace()) return; // navegación normal
      e.preventDefault();
      swapToTwin(href, card);
    });
  }
})();
